/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * VPS Playbooks Service
 * 
 * API client for VPS Playbook management endpoints.
 * Uses shared response types and handleApiResponse for consistent error handling.
 * 
 * For simple GET endpoints, components can use createQueryFn directly:
 *   import { createQueryFn } from '@shared/lib/api';
 *   const { data } = useQuery({
 *     queryKey: PLAYBOOK_QUERY_KEYS.index,
 *     queryFn: createQueryFn<PlaybookIndexResponseData>(PLAYBOOK_ENDPOINTS.index),
 *   });
 */

import { authenticatedFetch, createQueryFn, createMutationFn } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';

// ============================================================================
// API Endpoints - Centralized endpoint definitions
// ============================================================================

export const PLAYBOOK_ENDPOINTS = {
  list: '/api/playbooks',
  index: '/api/playbooks/index',
  indexRebuild: '/api/playbooks/index/rebuild',
  stats: '/api/playbooks/stats',
  semanticSearchStatus: '/api/playbooks/semantic-search/status',
  search: '/api/playbooks/search',
  semanticSearch: '/api/playbooks/semantic-search',
  read: (id: string) => `/api/playbooks/${id}`,
  freshness: (id: string) => `/api/playbooks/${id}/freshness`,
  prepareTask: '/api/playbooks/prepare-task',
  indexDirectory: '/api/playbooks/index-directory',
  indexedDirectories: '/api/playbooks/indexed-directories',
  agentsMd: '/api/playbooks/agents-md',
} as const;

// ============================================================================
// Query Keys - For React Query cache management
// ============================================================================

export const PLAYBOOK_QUERY_KEYS = {
  all: ['playbooks'] as const,
  list: ['playbooks', 'list'] as const,
  index: ['playbooks', 'index'] as const,
  stats: ['playbooks', 'stats'] as const,
  semanticSearchStatus: ['playbooks', 'semantic-search-status'] as const,
  detail: (id: string) => ['playbooks', 'detail', id] as const,
  freshness: (id: string) => ['playbooks', 'freshness', id] as const,
  search: (query: string) => ['playbooks', 'search', query] as const,
} as const;

import type {
  PlaybookIndexEntry,
  PlaybookReadResult,
  PlaybookFreshness,
} from '@shared/schema/playbooks';
import type {
  ListPlaybooksResponseData,
  LoadPlaybooksResponseData,
  SearchPlaybooksResponseData,
  PlaybookIndexResponseData,
  PrepareTaskResponseData,
  ReadPlaybookResponseData,
} from '@shared/types/responses/playbooks';

// Re-export shared types for convenience
export type {
  ListPlaybooksResponseData,
  LoadPlaybooksResponseData,
  SearchPlaybooksResponseData,
  PlaybookIndexResponseData as PlaybookIndexResponse,
  PrepareTaskResponseData as PrepareTaskResponse,
};

// Additional types for stats and semantic search status
export interface PlaybookStatsResponse {
  index: {
    playbooksCount: number;
    indexAge: number | null;
    indexExists: boolean;
  };
  config?: {
    tokenBudgets?: Record<string, number>;
  };
}

export interface IndexedDirectory {
  path: string;
  displayName: string;
  lastIndexed?: string;
}

export interface SemanticSearchStatusResponse {
  available: boolean;
  indexedDirectoryCount?: number;
  directories?: IndexedDirectory[];
}

export interface AgentsMdGenerateResponse {
  success: boolean;
  content?: string;
  tokenCount?: number;
  includedPlaybooks?: {
    id: string;
    title: string;
    path: string;
    inlined: boolean;
  }[];
  includedSkills?: {
    path: string;
    name: string;
  }[];
  error?: string;
}

// ============================================================================
// Query/Mutation Function Factories - For direct use in components
// ============================================================================

import type { PlaybookIndex } from '@shared/schema/playbooks';

/**
 * Query function for playbook index - use with useQuery
 * 
 * Transforms the API response from PlaybookIndex format (with organizational/products)
 * to PlaybookIndexResponseData format (with flat entries array) for UI consumption.
 */
export const playbookIndexQueryFn = async (): Promise<PlaybookIndexResponseData> => {
  const response = await authenticatedFetch(PLAYBOOK_ENDPOINTS.index);
  const index = await handleApiResponse<PlaybookIndex>(response);
  
  // Flatten organizational and products into a single entries array
  const entries: PlaybookIndexEntry[] = [];
  const byScope: Record<string, number> = { organizational: 0, product: 0 };
  const byDomain: Record<string, number> = {};
  let totalTokens = 0;
  
  // Add organizational playbooks
  if (index.organizational) {
    for (const domain of Object.keys(index.organizational)) {
      const domainPlaybooks = index.organizational[domain as keyof typeof index.organizational];
      if (domainPlaybooks) {
        entries.push(...domainPlaybooks);
        byScope.organizational += domainPlaybooks.length;
        byDomain[domain] = (byDomain[domain] || 0) + domainPlaybooks.length;
        totalTokens += domainPlaybooks.reduce((sum, p) => sum + (p.tokens || 0), 0);
      }
    }
  }
  
  // Add product playbooks
  if (index.products) {
    for (const productId of Object.keys(index.products)) {
      const product = index.products[productId];
      if (product?.playbooks) {
        entries.push(...product.playbooks);
        byScope.product += product.playbooks.length;
        for (const pb of product.playbooks) {
          if (pb.domain) {
            byDomain[pb.domain] = (byDomain[pb.domain] || 0) + 1;
          }
          totalTokens += pb.tokens || 0;
        }
      }
    }
  }
  
  return {
    entries,
    stats: {
      totalPlaybooks: entries.length,
      byScope,
      byDomain,
      totalTokens,
    },
    generatedAt: index.lastUpdated,
  };
};

/** Query function for playbook stats - use with useQuery */
export const playbookStatsQueryFn = createQueryFn<PlaybookStatsResponse>(PLAYBOOK_ENDPOINTS.stats);

/** Query function for semantic search status - use with useQuery */
export const semanticSearchStatusQueryFn = createQueryFn<SemanticSearchStatusResponse>(PLAYBOOK_ENDPOINTS.semanticSearchStatus);

/** Mutation function for rebuilding playbook index - use with useMutation */
export const rebuildPlaybookIndexMutationFn = createMutationFn<PlaybookIndexResponseData, void>(PLAYBOOK_ENDPOINTS.indexRebuild, 'POST');

// ============================================================================
// Service Functions - For complex operations with transformations
// ============================================================================

/**
 * List all loaded playbooks
 */
export async function listPlaybooks(): Promise<PlaybookIndexEntry[]> {
  const response = await authenticatedFetch('/api/playbooks');
  const data = await handleApiResponse<ListPlaybooksResponseData>(response);
  return data.playbooks;
}

/**
 * Load playbooks from a directory
 */
export async function loadPlaybooks(
  velocityPlaybooksPath?: string,
  productPlaybooksPath?: string,
  productId?: string
): Promise<LoadPlaybooksResponseData> {
  const response = await authenticatedFetch('/api/playbooks/load', {
    method: 'POST',
    body: { velocityPlaybooksPath, productPlaybooksPath, productId },
  });
  return handleApiResponse<LoadPlaybooksResponseData>(response);
}

/**
 * Search playbooks by keyword
 */
export async function searchPlaybooks(
  query: string,
  options?: {
    scope?: 'all' | 'organizational' | 'product';
    domain?: string;
    productId?: string;
    limit?: number;
    includeTldr?: boolean;
  }
): Promise<SearchPlaybooksResponseData> {
  const params = new URLSearchParams({ query });
  if (options?.scope && options.scope !== 'all') params.set('scope', options.scope);
  if (options?.domain) params.set('domain', options.domain);
  if (options?.productId) params.set('productId', options.productId);
  if (options?.limit) params.set('limit', options.limit.toString());
  if (options?.includeTldr) params.set('includeTldr', 'true');

  const response = await authenticatedFetch(`/api/playbooks/search?${params}`);
  return handleApiResponse<SearchPlaybooksResponseData>(response);
}

/**
 * Semantic search for playbooks
 */
export async function semanticSearchPlaybooks(
  query: string,
  options?: {
    scope?: 'all' | 'organizational' | 'product';
    domain?: string;
    productId?: string;
    limit?: number;
    includeTldr?: boolean;
    minScore?: number;
  }
): Promise<SearchPlaybooksResponseData> {
  const response = await authenticatedFetch('/api/playbooks/semantic-search', {
    method: 'POST',
    body: {
      query,
      scope: options?.scope,
      domain: options?.domain,
      productId: options?.productId,
      limit: options?.limit ?? 10,
      includeTldr: options?.includeTldr ?? true,
      minScore: options?.minScore ?? 0.3,
    },
  });
  return handleApiResponse<SearchPlaybooksResponseData>(response);
}

/**
 * Get semantic search status
 */
export async function getSemanticSearchStatus(): Promise<SemanticSearchStatusResponse> {
  const response = await authenticatedFetch('/api/playbooks/semantic-search/status');
  return handleApiResponse<SemanticSearchStatusResponse>(response);
}

/**
 * Read a playbook by ID
 */
export async function readPlaybook(
  id: string,
  options?: {
    full?: boolean;
    includeMetadata?: boolean;
    section?: string;
  }
): Promise<PlaybookReadResult> {
  const params = new URLSearchParams();
  if (options?.full) params.set('full', 'true');
  if (options?.includeMetadata) params.set('includeMetadata', 'true');
  if (options?.section) params.set('section', options.section);

  const url = `/api/playbooks/${encodeURIComponent(id)}${params.toString() ? `?${params}` : ''}`;
  const response = await authenticatedFetch(url);
  return handleApiResponse<PlaybookReadResult>(response);
}

/**
 * Get the playbook index
 */
export async function getPlaybookIndex(): Promise<PlaybookIndexResponseData> {
  const response = await authenticatedFetch('/api/playbooks/index');
  return handleApiResponse<PlaybookIndexResponseData>(response);
}

/**
 * Rebuild the playbook index
 * 
 * @param velocityRepoPath - Path to the Velocity repo (server defaults to ~/.velocity/velocity-repo)
 * @param products - Optional product configurations for product playbooks
 * @param includeTldr - Whether to include TL;DR in index (defaults to true)
 */
export async function rebuildPlaybookIndex(
  velocityRepoPath?: string,
  products?: { id: string; title: string; docsPath: string }[],
  includeTldr?: boolean
): Promise<PlaybookIndexResponseData> {
  const response = await authenticatedFetch('/api/playbooks/index/rebuild', {
    method: 'POST',
    body: { 
      velocityRepoPath, // Server defaults to ~/.velocity/velocity-repo if not provided
      products, 
      includeTldr: includeTldr ?? true,
    },
  });
  return handleApiResponse<PlaybookIndexResponseData>(response);
}

/**
 * Get playbook statistics
 */
export async function getPlaybookStats(): Promise<PlaybookStatsResponse> {
  const response = await authenticatedFetch('/api/playbooks/stats');
  return handleApiResponse<PlaybookStatsResponse>(response);
}

/**
 * Get playbook freshness information
 */
export async function getPlaybookFreshness(id: string): Promise<PlaybookFreshness> {
  const response = await authenticatedFetch(`/api/playbooks/${encodeURIComponent(id)}/freshness`);
  return handleApiResponse<PlaybookFreshness>(response);
}

/**
 * Prepare a task with playbook context
 */
export async function prepareTask(
  taskDescription: string,
  options?: {
    maxPlaybooks?: number;
    includeSeeAlso?: boolean;
    taskType?: string;
    productId?: string;
  }
): Promise<PrepareTaskResponseData> {
  const response = await authenticatedFetch('/api/playbooks/prepare-task', {
    method: 'POST',
    body: {
      taskDescription,
      maxPlaybooks: options?.maxPlaybooks ?? 3,
      includeSeeAlso: options?.includeSeeAlso ?? true,
      taskType: options?.taskType,
      productId: options?.productId,
    },
  });
  return handleApiResponse<PrepareTaskResponseData>(response);
}

/**
 * Index a playbook directory for semantic search
 */
export async function indexPlaybookDirectory(directory: string): Promise<{ indexed: number }> {
  const response = await authenticatedFetch('/api/playbooks/index-directory', {
    method: 'POST',
    body: { directory },
  });
  return handleApiResponse<{ indexed: number }>(response);
}

/**
 * Get list of indexed directories
 */
export async function getIndexedDirectories(): Promise<string[]> {
  const response = await authenticatedFetch('/api/playbooks/indexed-directories');
  const data = await handleApiResponse<{ directories: string[] }>(response);
  return data.directories;
}

/**
 * Generate AGENTS.md content for a product
 * 
 * @param productId - Product ID (required)
 * @param options - Generation options
 * @param options.includePlaybooks - Include playbooks section (default: true)
 * @param options.includeSkills - Include skills section (default: true)
 * @param options.format - Output format: 'full' for detailed, 'tldr' for concise (default: 'full')
 */
export async function generateAgentsMd(
  productId: string,
  options?: {
    includePlaybooks?: boolean;
    includeSkills?: boolean;
    format?: 'full' | 'tldr';
  }
): Promise<AgentsMdGenerateResponse> {
  const response = await authenticatedFetch('/api/playbooks/agents-md/generate', {
    method: 'POST',
    body: {
      productId,
      includePlaybooks: options?.includePlaybooks ?? true,
      includeSkills: options?.includeSkills ?? true,
      format: options?.format ?? 'full',
    },
  });
  return handleApiResponse<AgentsMdGenerateResponse>(response);
}
